import {
  getSourceGroupValidationIssues,
  normalizeSourceGroupFields,
  sourceGroupTypeValues,
  type NormalizedSourceGroupFields,
  type SourceGroupApiDto,
  type SourceGroupCreatePayload,
  type SourceGroupFieldInput,
  type SourceGroupListResponse,
  type SourceGroupPatchPayload,
  type SourceGroupSupplierInstallationRefDto,
  type SourceGroupType,
} from '@artem-cbam-operator/common-server/source-group'
import { formatOrganizationSupplierInstallationLabel } from '@/features/suppliers/lib/supplier-installation-label'
import type { OrganizationSupplierInstallationListItemDto } from '@/features/suppliers/lib/supplier-installation-types'
import type { SupplierInstallationEmissionReportDto } from '@/features/suppliers/hooks/use-supplier-installation-emission-reports'

export {
  sourceGroupTypeValues,
  type SourceGroupApiDto,
  type SourceGroupApiDto as SourceGroupDto,
  type SourceGroupCreatePayload,
  type SourceGroupListResponse,
  type SourceGroupPatchPayload,
  type SourceGroupSupplierInstallationRefDto,
  type SourceGroupType,
}

export type SourceGroupSupplierInstallationOptionDto =
  OrganizationSupplierInstallationListItemDto

export type SourceGroupUpsertPayload = NormalizedSourceGroupFields

export type SourceGroupCreateFormInput = SourceGroupFormInput

export function formatSourceGroupSupplierInstallationLabel(
  option: SourceGroupSupplierInstallationOptionDto
): string {
  return formatOrganizationSupplierInstallationLabel(option)
}

export type SourceGroupFormInput = {
  type: SourceGroupType
  description: string
  country: string
  supplierInstallationId: string
  supplierInstallationEmissionReportId: string
}

function sourceGroupFieldInputFromForm(
  form: SourceGroupFormInput
): SourceGroupFieldInput {
  const description = form.description.trim() || null
  const supplierInstallationId = form.supplierInstallationId.trim() || null

  let country: string | null = form.country.trim() || null
  if (form.type === 'EXTERNAL_COUNTRY_KNOWN') {
    country =
      form.country.trim().length === 2
        ? form.country.trim().toLowerCase()
        : null
  }

  return {
    type: form.type,
    description,
    country,
    supplierInstallationId,
  }
}

function supplierInstallationEmissionReportIdFromForm(
  form: SourceGroupFormInput
): string | undefined {
  const supplierInstallationEmissionReportId =
    form.supplierInstallationEmissionReportId.trim()
  return supplierInstallationEmissionReportId.length > 0
    ? supplierInstallationEmissionReportId
    : undefined
}

export function toSourceGroupCreatePayload(
  form: SourceGroupFormInput
): SourceGroupCreatePayload {
  const fields = normalizeSourceGroupFields(sourceGroupFieldInputFromForm(form))

  return {
    ...fields,
    supplierInstallationEmissionReportId:
      supplierInstallationEmissionReportIdFromForm(form),
  }
}

export function toSourceGroupPatchPayload(
  form: SourceGroupFormInput
): SourceGroupPatchPayload {
  const fields = normalizeSourceGroupFields(sourceGroupFieldInputFromForm(form))
  const supplierInstallationEmissionReportId =
    supplierInstallationEmissionReportIdFromForm(form)

  return {
    ...fields,
    ...(supplierInstallationEmissionReportId
      ? { supplierInstallationEmissionReportId }
      : {}),
  }
}

export function filterSourceGroupEmissionReportsForCnCode(
  emissionReports: SupplierInstallationEmissionReportDto[],
  cnCode: string | undefined
): SupplierInstallationEmissionReportDto[] {
  const normalizedCnCode = cnCode?.trim()
  if (!normalizedCnCode) {
    return []
  }

  return emissionReports.filter((report) => report.cnCode === normalizedCnCode)
}

export function inferSourceGroupEmissionReportId(
  specificEmbeddedEmissions: number | null,
  emissionReports: SupplierInstallationEmissionReportDto[]
): string {
  if (specificEmbeddedEmissions == null || emissionReports.length === 0) {
    return ''
  }

  const matches = emissionReports.filter(
    (report) => report.specificEmbeddedEmissions === specificEmbeddedEmissions
  )

  return matches.length === 1 ? matches[0]!.id : ''
}

export function isSourceGroupFormValid(
  form: SourceGroupFormInput,
  options?: { installationHasEmissionReports?: boolean }
): boolean {
  if (
    getSourceGroupValidationIssues(
      normalizeSourceGroupFields(sourceGroupFieldInputFromForm(form))
    ).length > 0
  ) {
    return false
  }

  if (
    form.type === 'EXTERNAL_INSTALLATION_KNOWN' &&
    options?.installationHasEmissionReports &&
    !form.supplierInstallationEmissionReportId.trim()
  ) {
    return false
  }

  return true
}

export function isSourceGroupCreateFormValid(
  form: SourceGroupFormInput,
  options?: { installationHasEmissionReports?: boolean }
): boolean {
  return isSourceGroupFormValid(form, options)
}

export function toSourceGroupUpsertPayload(
  form: SourceGroupFormInput
): SourceGroupUpsertPayload {
  return normalizeSourceGroupFields(sourceGroupFieldInputFromForm(form))
}
